import { VirtualCardType } from 'modules/virtual-cards/constants'

import { VcnCardInUseStatus, VcnCardStatus } from './types'
import type { VcnManagementCardFilters } from './types'
import type { VcnCardMetrics } from '../../api/dto/vcn-card-metrics.dto'
import type { MetricTile } from './VcnManagementMetricTiles'

/**
 * Every clickable number in Card Mode. The string is the key the tile component
 * hands back on click, and {@link cardFiltersForMetric} turns it into filters.
 */
export enum VcnCardMetricKey {
  PRIMARY = 'primary',
  PRIMARY_WEX = 'primary:wex',
  PRIMARY_MARQETA = 'primary:marqeta',
  ACTIVE = 'active',
  ACTIVE_WEX = 'active:wex',
  ACTIVE_MARQETA = 'active:marqeta',
  ORPHAN = 'orphan',
  ORPHAN_ENDED = 'orphan:ended',
  ORPHAN_NOT_PRIMARY = 'orphan:not-primary',
}

/**
 * The filters each tile click applies.
 *
 * This is the whole correctness claim of the tiles: a number is only honest if
 * clicking it returns exactly that many rows. It holds because EPS classifies
 * both the metric and the list row through the same rule and excludes test users
 * from both — this table only has to name the right filter, it does not restate
 * the definition.
 *
 * Every metric counts ACTIVE cards, so `cardStatus` is pinned here rather than
 * inherited: a metric filter REPLACES the sidebar filters, so nothing is left to
 * inherit from.
 */
export function cardFiltersForMetric(
  key: VcnCardMetricKey
): VcnManagementCardFilters {
  const base: VcnManagementCardFilters = { cardStatus: VcnCardStatus.ACTIVE }
  switch (key) {
    // Primary = the current card of a live benefit.
    case VcnCardMetricKey.PRIMARY:
      return { ...base, inUse: [VcnCardInUseStatus.IN_USE] }
    case VcnCardMetricKey.PRIMARY_WEX:
      return {
        ...base,
        inUse: [VcnCardInUseStatus.IN_USE],
        vcnProvider: VirtualCardType.WEX,
      }
    case VcnCardMetricKey.PRIMARY_MARQETA:
      return {
        ...base,
        inUse: [VcnCardInUseStatus.IN_USE],
        vcnProvider: VirtualCardType.MARQETA,
      }

    // Active = every active card, whatever its benefit binding, so `inUse` is
    // deliberately unset rather than listing all three values.
    case VcnCardMetricKey.ACTIVE:
      return { ...base }
    case VcnCardMetricKey.ACTIVE_WEX:
      return { ...base, vcnProvider: VirtualCardType.WEX }
    case VcnCardMetricKey.ACTIVE_MARQETA:
      return { ...base, vcnProvider: VirtualCardType.MARQETA }

    // Orphan = active but NOT the current card of a live benefit, which is the
    // other two in-use states together.
    case VcnCardMetricKey.ORPHAN:
      return {
        ...base,
        inUse: [VcnCardInUseStatus.IN_USE_ENDED, VcnCardInUseStatus.NOT_IN_USE],
      }
    case VcnCardMetricKey.ORPHAN_ENDED:
      return { ...base, inUse: [VcnCardInUseStatus.IN_USE_ENDED] }
    case VcnCardMetricKey.ORPHAN_NOT_PRIMARY:
      return { ...base, inUse: [VcnCardInUseStatus.NOT_IN_USE] }
  }
}

/** True when the string is one of ours — the tile component deals in plain strings. */
export function isCardMetricKey(key: string): key is VcnCardMetricKey {
  return (Object.values(VcnCardMetricKey) as string[]).includes(key)
}

/** Names the active metric in the clear-filter banner. */
export const CARD_METRIC_LABELS: Record<VcnCardMetricKey, string> = {
  [VcnCardMetricKey.PRIMARY]: 'Primary cards',
  [VcnCardMetricKey.PRIMARY_WEX]: 'Primary cards · WEX',
  [VcnCardMetricKey.PRIMARY_MARQETA]: 'Primary cards · MARQETA',
  [VcnCardMetricKey.ACTIVE]: 'Active cards',
  [VcnCardMetricKey.ACTIVE_WEX]: 'Active cards · WEX',
  [VcnCardMetricKey.ACTIVE_MARQETA]: 'Active cards · MARQETA',
  [VcnCardMetricKey.ORPHAN]: 'Orphan cards',
  [VcnCardMetricKey.ORPHAN_ENDED]: 'Orphan cards · ended benefit',
  [VcnCardMetricKey.ORPHAN_NOT_PRIMARY]: 'Orphan cards · not primary',
}

const ORPHAN_OVERLAP_NOTE =
  'Not primary counts the same cards as the surplus on multi-card benefits, per card instead of per benefit.'

/**
 * Tile configs for Card Mode. `undefined` counts while metrics load, so the
 * tiles render skeletons rather than zeros.
 *
 * The provider tiles show their two readings and no combined total: every
 * number on a tile has to be clickable to exactly its own rows, and a
 * both-providers total would be a number with nothing behind it.
 */
export function buildCardMetricTiles(
  metrics: VcnCardMetrics | undefined
): MetricTile[] {
  return [
    {
      key: VcnCardMetricKey.PRIMARY,
      title: 'Primary Cards',
      tooltip:
        'Active cards that are the current card of a live benefit — the cards actually in use. A subset of Active cards.',
      caption: 'Number of primary virtual cards by provider',
      values: [
        {
          key: VcnCardMetricKey.PRIMARY_WEX,
          pill: { label: 'WEX', tone: 'wex' },
          count: metrics?.primaryCards.wexCount,
          ariaLabel: 'Primary cards, WEX',
        },
        {
          key: VcnCardMetricKey.PRIMARY_MARQETA,
          pill: { label: 'MARQETA', tone: 'marqeta' },
          count: metrics?.primaryCards.marqetaCount,
          ariaLabel: 'Primary cards, MARQETA',
        },
      ],
    },
    {
      key: VcnCardMetricKey.ACTIVE,
      title: 'Active cards',
      tooltip:
        'Every active card, whatever benefit it is bound to. Equals Primary cards plus Orphan cards.',
      caption: 'Currently active virtual cards by provider',
      values: [
        {
          key: VcnCardMetricKey.ACTIVE_WEX,
          pill: { label: 'WEX', tone: 'wex' },
          count: metrics?.activeCards.wexCount,
          ariaLabel: 'Active cards, WEX',
        },
        {
          key: VcnCardMetricKey.ACTIVE_MARQETA,
          pill: { label: 'MARQETA', tone: 'marqeta' },
          count: metrics?.activeCards.marqetaCount,
          ariaLabel: 'Active cards, MARQETA',
        },
      ],
    },
    {
      key: VcnCardMetricKey.ORPHAN,
      title: 'Needs Cleanup Orphan',
      tooltip: `Active cards that no live benefit points at — the cleanup list. ${ORPHAN_OVERLAP_NOTE}`,
      caption: 'Orphaned or stale cards to terminate',
      values: [
        {
          key: VcnCardMetricKey.ORPHAN,
          count: metrics?.orphanCards.totalCount,
          ariaLabel: 'Orphan cards needing cleanup',
        },
      ],
    },
    {
      key: VcnCardMetricKey.ORPHAN_ENDED,
      title: 'Orphan under end benefit',
      tooltip:
        'An active card whose benefit has already ended. The grid shows these as "On Ended Plan".',
      caption: 'Benefit has ended but the card is still active',
      values: [
        {
          key: VcnCardMetricKey.ORPHAN_ENDED,
          count: metrics?.orphanCards.endedBenefitCount,
          ariaLabel: 'Orphan cards under an ended benefit',
        },
      ],
    },
    {
      key: VcnCardMetricKey.ORPHAN_NOT_PRIMARY,
      title: 'Orphan not primary',
      tooltip:
        'An active card that a live benefit no longer points at — typically a terminate-and-new whose terminate half did not land.',
      caption: 'Not the primary card of any benefit',
      values: [
        {
          key: VcnCardMetricKey.ORPHAN_NOT_PRIMARY,
          count: metrics?.orphanCards.notPrimaryCount,
          ariaLabel: 'Orphan cards that are not primary',
        },
      ],
    },
  ]
}
